// frontend/src/pages/EmojiTranslatorPage.js
//
// /tools/emoji-translator
//
// MEASURED 2026-08-19. `emoji translator` 2,900/mo, KD 12, WALL 2, and no AI
// Overview — which is now rare enough to be worth stating.
//
// WHY THIS ONE AND NOT THE OTHER NOVELTY TOOLS. Two of the five results are
// Hootsuite and Pallyy: companies in our own category maintaining this exact
// page. That is the test that `gamertag generator` failed — its SERP was
// gamerbolt and r/teenagers. When two competitors keep a tool alive, the traffic
// converts well enough to be worth the maintenance.
//
// Top 5, SERP-checked 2026-08-19: emojitranslate.com, a Play Store listing,
// hootsuite.com, ravbug.com, pallyy.com.
//
// THE DIFFERENTIATOR IS THE READOUT, NOT THE TRANSLATION. Word-to-emoji is a
// trivial build and five sites already do it. None of them show how the result
// is HEARD. Verified 2026-08-19 against RNIB and Scope for Business, whose
// guidance is specific: no more than three emoji, placed at the END of a caption
// rather than mid-sentence, never carrying essential meaning, and check the name
// because it may differ from what you meant. This page renders the spoken output
// and measures against exactly that. Same honesty position as the LinkedIn
// formatter, and it is becoming the through-line for our tool pages.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Button, Accordion, Image } from 'react-bootstrap'
import { CheckCircleFill } from 'react-bootstrap-icons'

import Seo from '../components/Seo'
import { getSeoMeta } from '../data/seoMeta'
import EmojiTranslator from '../components/EmojiTranslator'

const URL = '/tools/emoji-translator'
const HERO = '/assets/images/articles/emoji-translator.webp'

// Drives the visible accordion. The FAQPage schema in seo-meta.json was
// generated from this array — see LinkedInFormatterPage.js for the regeneration
// command, which works here by swapping the two paths.
export const FAQS = [
  ['What is an emoji translator?',
   'A tool that converts words into emoji, or emoji back into words. Type a sentence and it swaps the words it recognises for the matching picture — or, in the other direction, tells you what a string of emoji actually says.'],
  ['How do screen readers read emoji?',
   'Aloud, by name, in the position the emoji sits. "Big news 🔥🔥🔥" is announced as "Big news fire fire fire". The names come from the Unicode CLDR list rather than from what you meant by the picture, which is why an emoji that reads clearly on screen can be confusing or comic when heard. The tool above shows you the spoken version.'],
  ['How many emoji should I use in a caption?',
   'RNIB and Scope both advise no more than three, placed at the end of the caption rather than inside a sentence. Emoji scattered mid-sentence interrupt the flow when read aloud, so a caption that looks lively on screen becomes hard to follow. Three at the end is the practical rule.'],
  ['Do emoji hurt accessibility?',
   'They cost something, and how much depends entirely on how you use them. A couple at the end of a post is a reasonable trade. A line of twenty, or emoji used in place of words, makes the caption genuinely tiring to listen to — and if an emoji carries meaning the text does not repeat, that meaning is lost for anyone who cannot see it.'],
  ['Do emoji help engagement on social media?',
   'They help scanning, which is not quite the same thing. An emoji breaks up a wall of text and marks where a new idea starts, and that earns attention. What does not work is replacing words with pictures, because the reader has to decode them, and decoding is friction.'],
  ['Why do emoji look different on iPhone and Android?',
   'Each platform draws its own artwork for the same character. The code you send is identical; Apple, Google, Samsung and Microsoft each render it their own way. Meanings can shift between designs, so an emoji that reads one way on your phone may not on someone else’s.'],
  ['Can I use emoji in an Instagram or TikTok bio?',
   'Yes — bios accept emoji on every major network, and unlike Unicode pseudo-bold they are read correctly by assistive software. They do count toward the character limit, so on TikTok’s 80-character bio a few emoji is a real share of the space.'],
  ['Does this tool work offline or send my text anywhere?',
   'Nothing leaves your browser. The dictionary ships with the page and the translation happens on your device, so there is no upload, no account and no limit on how many times you use it.'],
]

export default function EmojiTranslatorPage () {
  return (
    <>
      <Seo {...getSeoMeta(URL)} image={HERO} />

      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-4'>
            <Col lg={7}>
              <h1 className='display-5 fw-bold mb-3'>Emoji translator</h1>
              <p className='lead mb-0'>
                Turn words into emoji, or emoji back into words — and hear how the result is
                read aloud, which is the part that decides whether it works.
              </p>
            </Col>
            <Col lg={5}>
              <Image src={HERO} alt='An emoji keyboard open on a phone screen' fluid rounded
                     loading='eager' width={1600} height={1066} className='shadow-sm' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── THE ANSWER FIRST ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            An <strong>emoji translator</strong> swaps the words it recognises for the matching
            emoji — and, in the other direction, tells you what a string of emoji actually says.
            This one runs entirely in your browser, in both directions, with no account and no
            limit.
          </p>
          <p className='text-muted fs-5 mb-0'>
            It also does something the other emoji translators do not: it shows you the caption
            as a screen reader will announce it. Every emoji is read aloud by name, so
            “Big news 🔥🔥🔥” arrives as “Big news fire fire fire”. That readout is usually the
            moment people change how many they use.
          </p>
        </Container>
      </section>

      {/* ───────── THE TOOL ───────── */}
      <section className='pb-5'>
        <Container fluid='lg' style={{ maxWidth: 1000 }}>
          <EmojiTranslator />
        </Container>
      </section>

      {/* ───────── THE RULES ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 900 }}>
          <h2 className='fw-bold mb-4'>What the accessibility guidance actually says</h2>
          <div className='table-responsive'>
            <table className='table table-bordered align-middle bg-white mb-0'>
              <tbody>
                {[
                  ['How many', 'Three at most', 'RNIB and Scope both give the same number.'],
                  ['Where', 'At the end of the caption', 'Mid-sentence emoji interrupt the reading.'],
                  ['Repeated emoji', 'Read out every time', '🔥🔥🔥 is heard as “fire fire fire”.'],
                  ['Carrying meaning', 'Never on their own', 'If the text does not say it, it is lost.'],
                  ['The name', 'May not match your intent', 'Check it — the readout above shows it.'],
                  ['In place of words', 'Avoid', 'Decoding pictures is friction for everyone.'],
                  ['Emoji vs pseudo-bold', 'Emoji are the safer choice', 'They are read correctly; Unicode bold is not.'],
                  ['Character limits', 'Emoji count', 'A real share of an 80-character TikTok bio.'],
                ].map(([label, value, note]) => (
                  <tr key={label}>
                    <td className='fw-semibold' style={{ width: '26%' }}>{label}</td>
                    <td className='text-primary fw-semibold' style={{ width: '26%' }}>{value}</td>
                    <td className='text-muted'>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted small mt-3 mb-0'>
            Sources: <a href='https://www.rnib.org.uk/living-with-sight-loss/assistive-aids-and-technology/tv-audio-and-gaming/guide-to-accessible-social-media/'
                        target='_blank' rel='noopener noreferrer nofollow'>RNIB</a> and{' '}
            <a href='https://business.scope.org.uk/8-ways-you-can-make-your-twitter-feed-more-accessible/'
               target='_blank' rel='noopener noreferrer nofollow'>Scope for Business</a>.
          </p>
        </Container>
      </section>

      {/* ───────── DEPTH ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          {[
            ['Emoji are the accessible option — pseudo-bold is not',
             'It is worth being clear about this, because the two get lumped together as "decorative text". Emoji are real characters with real names, and assistive software reads them correctly; the cost is only that it reads all of them. Unicode pseudo-bold — the trick used to fake bold text on LinkedIn and Instagram — is a different thing entirely, and screen readers either announce it letter by letter or skip it. If you are choosing between decorating a caption with emoji or with fake bold, the emoji is the better citizen by a distance.'],
            ['The name is not always the meaning',
             'Emoji are named by the Unicode consortium, not by how people use them. Several of the most-used emoji have names that will surprise you when heard aloud, and a few carry connotations in common use that the official name says nothing about. The readout in the tool above is the fastest way to catch this before you post rather than after.'],
            ['Where they genuinely help',
             'An emoji at the start of a line acts as a bullet and makes a list scannable. One at the end of a post adds tone that plain text struggles with. Both are cheap and both survive being read aloud. What does not work is substituting pictures for words in the body of a sentence, which forces every reader to decode rather than read.'],
            ['They count toward the character limit',
             'On an 80-character TikTok bio, four emoji is five per cent of the space. On a 150-character Instagram bio it is less painful but still real. If you are working against a tight limit, spend the characters on the words that say what you do, and add the emoji only if there is room left.'],
          ].map(([heading, body]) => (
            <div className='mb-4' key={heading}>
              <h2 className='fw-bold h4 mb-2'>{heading}</h2>
              <p className='text-muted mb-0'>{body}</p>
            </div>
          ))}
        </Container>
      </section>

      {/* ───────── WHAT IS GREATLY SOCIAL (standing rule 4) ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 940 }}>
          <Row className='align-items-center g-4'>
            <Col md={6}>
              <h2 className='fw-bold mb-3'>What is Greatly Social?</h2>
              <p className='text-muted'>
                Greatly Social is a social media scheduler and content calendar. At Greatly
                Social you can describe your business once, let AI draft the month — captions
                and images — and schedule it to all eight networks from one calendar.
              </p>
              <p className='text-muted'>
                Plans start at $9/month and the trial needs no card.
              </p>
              <Button variant='primary' href='/register' className='me-2'>
                Start using Greatly Social free
              </Button>
              <Button variant='outline-primary' href='/plans'>See plans</Button>
            </Col>
            <Col md={6}>
              {/* A real capture of the product, not stock — standing rule 2. */}
              <Image src='/assets/logo/aipost.webp' alt='AI drafting a caption in Greatly Social'
                     fluid rounded className='shadow-sm' loading='lazy' />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── CROSS-LINKS (standing rule 8) ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold h4 mb-3'>More free tools</h2>
          <Row className='g-3'>
            {[['/tools/linkedin-text-formatter', 'LinkedIn text formatter', 'Bold and italic, with the real cost shown'],
              ['/tools/instagram-caption-generator', 'Caption generator', 'Every network at once'],
              ['/tools/instagram-bio-generator', 'Bio generator', 'Fits the character limit'],
              ['/tools/instagram-name-generator', 'Name generators', 'Handles for every network'],
              ['/tools/instagram-post-size', 'Instagram post size', 'Dimensions and a resizer'],
              ['/tools', 'All free tools', 'The full set']].map(([href, title, note]) => (
              <Col md={4} key={href}>
                <a href={href} className='d-block p-3 rounded-3 text-decoration-none h-100'
                   style={{ border: '1px solid #e5e7eb', background: '#fff' }}>
                  <div className='fw-semibold'>{title}</div>
                  <div className='text-muted small'>{note}</div>
                </a>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── PRODUCT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg' style={{ maxWidth: 880 }}>
          <h2 className='fw-bold mb-3'>Writing the caption is the small part</h2>
          <p className='text-muted fs-5'>
            Doing it three times a week, for eight networks, for a year — that is the part that
            stops people. Greatly Social does that bit.
          </p>
          <Row className='g-3 mb-4'>
            {[['Describe it once, get the month', 'AI drafts the posts; you edit rather than write.'],
              ['Eight networks, one post', 'A separate caption per network on the same post.'],
              ['A real content calendar', 'Drag to reschedule. Empty days are obvious.'],
              ['Link in bio included', 'A hosted page that updates with your latest posts.']].map(([t, b]) => (
              <Col md={6} key={t}>
                <div className='d-flex align-items-start gap-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <div><div className='fw-semibold'>{t}</div><div className='text-muted small'>{b}</div></div>
                </div>
              </Col>
            ))}
          </Row>
          <Button variant='primary' size='lg' href='/register' className='me-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-primary' size='lg' href='/plans'>See plans from $9/month</Button>
        </Container>
      </section>

      {/* ───────── FAQ (standing rule 5) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Common questions</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], i) => (
              <Accordion.Item eventKey={String(i)} key={i}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA (standing rule 3) ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can write it once and post it everywhere</h2>
          <p className='lead mb-4'>
            AI drafts a caption for each network, you edit, and the calendar publishes it. Free
            for 14 days, no card, plans from $9/month.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start using Greatly Social free
          </Button>
          <Button variant='outline-light' size='lg' href='/calendar-view' className='mb-2'>
            See the calendar
          </Button>
        </div>
      </section>
    </>
  )
}
